import React from 'react';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useIsAccountEnabled } from 'src/hooks/use-is-account-enabled';
import { RemoteComponentSlot, useRemoteComponent } from 'src/hooks/use-remote-component';
import { Navigation } from 'src/dispensary/core-menu/components/header/navigation';
import StoreFrontHeader from 'src/dispensary/core-menu/components/core-menu-header/store-front-header';

import { resolveSurfaceColor } from '../fields/tokens';
import { PageSeoHead } from './page-seo-head';

type PageRootProps = {
  backgroundColor?: string;
  children: React.ReactNode;
  showHeader?: boolean;
  showNavigation?: boolean;
  seoTitle?: string;
  seoDescription?: string;
  hideFromSearch?: boolean;
  /** Puck's own injected context -- absent in some direct test renders, present everywhere real. */
  puck?: { isEditing?: boolean };
};

function isRegionEnabled(value: unknown): boolean {
  return value !== false;
}

/**
 * Shared root render for the CMS editors (standalone page + menu landing page).
 * Conditionally shows the StoreFront header and navigation around the page content.
 */
export function PageRootRender({
  backgroundColor = 'surface',
  children,
  showHeader = true,
  showNavigation = true,
  seoTitle = '',
  seoDescription = '',
  hideFromSearch = false,
  puck,
}: PageRootProps): JSX.Element {
  const headerEnabled = isRegionEnabled(showHeader);
  const navEnabled = isRegionEnabled(showNavigation);
  const resolvedBackgroundColor = resolveSurfaceColor(backgroundColor);
  const pageBackgroundColor =
    backgroundColor === 'surface' ? `var(--dcm-cms-page-bg, ${resolvedBackgroundColor})` : resolvedBackgroundColor;

  const { dispensary } = useDispensary();
  const isAccountEnabled = useIsAccountEnabled();
  const ResolvedNavigation = useRemoteComponent(RemoteComponentSlot.Navigation, { fallback: Navigation });

  return (
    <div
      style={
        {
          '--cms-page-background-color': pageBackgroundColor,
          backgroundColor:
            'var(--cms-page-background-color, var(--dcm-cms-page-bg, var(--dcm-color-surface, #ffffff)))',
          minHeight: '100vh',
          width: '100%',
        } as React.CSSProperties
      }
    >
      {/* Metadata is production-only (ECOMM-14849): Puck's editor canvas renders this root inside
          its own iframe, and next/head has no reliable target document there -- the author-facing
          SEO preview is deliberately out of scope for this ticket, so there is nothing lost by
          skipping it in editor mode. */}
      {!puck?.isEditing && (
        <PageSeoHead
          seoTitle={seoTitle}
          seoDescription={seoDescription}
          hideFromSearch={hideFromSearch}
          fallbackTitle={dispensary?.name ?? ''}
        />
      )}
      {headerEnabled && <StoreFrontHeader />}
      {navEnabled && dispensary && <ResolvedNavigation dispensary={dispensary} isAccountEnabled={isAccountEnabled} />}
      {children}
    </div>
  );
}
